import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, ChevronRight, Share2, ArrowLeft, Tag, Mail, User, Headphones, QrCode } from 'lucide-react';
import { eventsAPI, promoterAPI, referralStore } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import ReviewsSection from './ReviewsSection';
import DiscussionsSection from './DiscussionsSection';
import { CATEGORY_LABELS } from '../lib/eventMeta';
import ContactOrganizerModal from '../components/ContactOrganizerModal';

export default function EventDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [showContact, setShowContact] = useState(false);

  // A promoter link can also arrive as /events/:id?ref=CODE (e.g. a shared URL).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!ref) return;
    promoterAPI.trackClick(ref)
      .then(r => referralStore.set(id, r.data.referral_code))
      .catch(() => {});
  }, [searchParams, id]);

  useEffect(() => {
    eventsAPI.get(id)
      .then(r => {
        setEvent(r.data);
        const initQty = {};
        (r.data.ticket_types || []).forEach(tt => { initQty[tt.type_id] = 0; });
        setQuantities(initQty);
      })
      .catch(() => navigate('/explore'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const totalItems = Object.values(quantities).reduce((a, b) => a + b, 0);
  const totalPrice = event?.ticket_types?.reduce((sum, tt) => sum + (quantities[tt.type_id] || 0) * tt.price, 0) || 0;

  const handleCheckout = () => {
    const items = (event.ticket_types || [])
      .filter(tt => (quantities[tt.type_id] || 0) > 0)
      .map(tt => ({ ticket_type_id: tt.type_id, quantity: quantities[tt.type_id] }));
    if (items.length === 0) return;
    navigate('/checkout', { state: { event, items } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center">
        <div className="text-gray-400">{t('loading')}</div>
      </div>
    );
  }

  if (!event) return null;

  const minPrice = event.ticket_types?.length ? Math.min(...event.ticket_types.map(t => t.price)) : 0;

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="event-detail-page">
      {/* Hero image */}
      <div className="relative h-64 md:h-96 overflow-hidden">
        <img
          src={event.image_url || 'https://images.unsplash.com/photo-1765278797923-10a027f5c69d?w=1200&q=80'}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-4 md:px-8 pb-8">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-300 hover:text-white text-sm mb-4 transition-colors">
              <ArrowLeft size={16} /> {t('back')}
            </button>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-red-600/20 text-red-400 border border-red-600/30">
                {CATEGORY_LABELS[event.category] || event.category}
              </span>
              {event.is_featured && (
                <span className="gold-badge text-xs px-2 py-0.5 rounded-sm">Featured</span>
              )}
            </div>
            <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {event.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event info */}
          <div className="lg:col-span-2">
            {/* Quick info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: Calendar, label: 'Date', value: event.date },
                { icon: Clock, label: 'Time', value: event.time || 'TBA' },
                { icon: MapPin, label: 'Location', value: event.city },
                { icon: Tag, label: 'Category', value: CATEGORY_LABELS[event.category] || event.category },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Icon size={14} />
                    <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
                  </div>
                  <p className="text-white font-semibold text-sm truncate">{value}</p>
                </div>
              ))}
            </div>

            {/* Description */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-6 mb-6">
              <h2 className="text-lg font-black uppercase mb-4 text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>About This Event</h2>
              <p className="text-gray-300 leading-relaxed">{event.description}</p>
            </div>

            {/* Venue */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-6">
              <h2 className="text-lg font-black uppercase mb-4 text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>Venue</h2>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">{event.location}</p>
                  <p className="text-gray-400 text-sm">{event.city}, {event.country}</p>
                </div>
              </div>
            </div>

            {/* Lineup */}
            {event.lineup?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">LINEUP</h3>
                <div className="flex flex-wrap gap-2">
                  {event.lineup.map(dj => {
                    const inner = (
                      <>
                        <Headphones size={14} className="text-red-500" />
                        <span className="text-white text-sm font-bold">{dj.name}</span>
                        {dj.social_links?.instagram && <span className="text-gray-500 text-xs">{dj.social_links.instagram}</span>}
                        {dj.series_wide && <span className="text-gray-600 text-[10px] uppercase">resident</span>}
                      </>
                    );
                    return dj.has_profile ? (
                      <Link key={dj.assignment_id} to={`/artists/${dj.user_id}`} className="flex items-center gap-2 border border-gray-700 hover:border-red-500 rounded-full px-3 py-1.5">{inner}</Link>
                    ) : (
                      <span key={dj.assignment_id} className="flex items-center gap-2 border border-gray-800 rounded-full px-3 py-1.5">{inner}</span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Owner / team controls, shown according to the viewer's permissions */}
            {event.my_permissions?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 mt-6 flex flex-wrap items-center gap-3">
                <span className="text-[11px] font-black tracking-widest text-gray-500 mr-auto">EVENT TEAM CONTROLS</span>
                <Link to={`/team/event/${event.event_id}`} className="flex items-center gap-2 border border-gray-700 hover:border-red-500 text-white text-sm px-3 py-1.5 rounded">
                  <Users size={14} /> Team & lineup
                </Link>
                {event.my_permissions.includes('checkin') && (
                  <Link to={`/check-in/${event.event_id}`} className="flex items-center gap-2 border border-gray-700 hover:border-red-500 text-white text-sm px-3 py-1.5 rounded">
                    <QrCode size={14} /> Check-in
                  </Link>
                )}
                {event.recurring_parent_id && (
                  <Link to={`/team/series/${event.recurring_parent_id}`} className="flex items-center gap-2 border border-gray-700 hover:border-red-500 text-white text-sm px-3 py-1.5 rounded">
                    <Users size={14} /> Series team
                  </Link>
                )}
              </div>
            )}

            {/* Organizer */}
            {event.organiser_id && (
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-6 mt-6 flex items-center justify-between flex-wrap gap-4">
                <Link to={`/organizers/${event.organiser_id}`} className="flex items-center gap-3 group">
                  <div className="w-11 h-11 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider">Organized by</p>
                    <p className="text-white font-semibold text-sm group-hover:text-red-400 transition-colors">{event.organiser_name}</p>
                  </div>
                </Link>
                <button
                  onClick={() => setShowContact(true)}
                  className="flex items-center gap-2 border border-gray-700 hover:border-red-500 text-white text-sm px-4 py-2 rounded transition-colors"
                  data-testid="contact-organizer-btn"
                >
                  <Mail size={14} /> Contact Organizer
                </button>
              </div>
            )}

            {/* Tags */}
            {event.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {event.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Reviews / Conversational Space */}
            <ReviewsSection eventId={id} />
            <DiscussionsSection eventId={id} />
          </div>

          {/* Ticket selector sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 bg-[#0A0A0A] border border-gray-800 rounded-xl p-6" data-testid="ticket-selector">
              <h3 className="text-lg font-black uppercase text-white mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('selectTickets')}
              </h3>
              <p className="text-gray-500 text-xs mb-6">Select ticket type and quantity</p>

              {(event.ticket_types || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No tickets available</p>
              ) : (
                <div className="space-y-3 mb-6">
                  {(event.ticket_types || []).map(tt => (
                    <div key={tt.type_id} className="border border-gray-800 rounded-lg p-4" data-testid={`ticket-type-${tt.type_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-semibold text-sm">{tt.name}</p>
                          <p className="font-bold text-base" style={{ color: '#E63946' }}>
                            {tt.price === 0 ? <span className="text-green-400">Free</span> : format(tt.price)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setQuantities(q => ({ ...q, [tt.type_id]: Math.max(0, (q[tt.type_id] || 0) - 1) }))}
                            className="w-7 h-7 rounded border border-gray-700 flex items-center justify-center text-gray-400 hover:border-red-500 hover:text-white transition-colors text-lg font-bold"
                            data-testid={`dec-${tt.type_id}`}
                          >
                            −
                          </button>
                          <span className="w-6 text-center text-white font-bold text-sm" data-testid={`qty-${tt.type_id}`}>
                            {quantities[tt.type_id] || 0}
                          </span>
                          <button
                            onClick={() => setQuantities(q => ({ ...q, [tt.type_id]: Math.min(tt.available || 10, (q[tt.type_id] || 0) + 1) }))}
                            className="w-7 h-7 rounded border border-gray-700 flex items-center justify-center text-gray-400 hover:border-red-500 hover:text-white transition-colors text-lg font-bold"
                            data-testid={`inc-${tt.type_id}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      {tt.available && <p className="text-gray-600 text-xs">{tt.available} available</p>}
                    </div>
                  ))}
                </div>
              )}

              {totalItems > 0 && (
                <div className="border-t border-gray-800 pt-4 mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">{totalItems} ticket{totalItems > 1 ? 's' : ''}</span>
                    <span className="text-white font-bold">{format(totalPrice)}</span>
                  </div>
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={totalItems === 0}
                className="w-full btn-primary py-3 rounded text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="checkout-btn"
              >
                {t('buyTickets')} {totalItems > 0 && `(${totalItems})`}
                <ChevronRight size={16} />
              </button>

              <p className="text-gray-600 text-xs text-center mt-3">
                Secured by Stripe · Guest checkout available
              </p>
            </div>
          </div>
        </div>
      </div>

      {showContact && (
        <ContactOrganizerModal
          organizerId={event.organiser_id}
          organizerName={event.organiser_name}
          eventId={event.event_id}
          onClose={() => setShowContact(false)}
        />
      )}
    </div>
  );
}
